import tw, { theme } from 'twin.macro';
import { useTranslation, Trans } from 'react-i18next';

import { Spacer } from '@components/Spacer';
import { NoSearchResult as NoSearchResultIcon } from '@icons/NoSearchResult';
import { NoData as NoDataIcon } from '@icons/NoData';
import { NoResults } from '@icons/NoResults';

const textStyles = 'text-[13px] text-left text-text-and-icon-muted-2 font-light';

const Container = tw.div`w-[283px] h-full m-auto flex flex-col items-center justify-center mt-[50px]`;
const Card = tw.div`w-full bg-gray-2 p-3 rounded-[8px]`;
const Title = tw.h3`text-[18px] text-text-and-icon-muted`;
const SmallText = tw.span`${textStyles} text-wrap`;
const List = tw.ul`m-0 box-border w-full px-2`;
const ListItem = tw.li`${textStyles} relative leading-5 pl-3 before:inline-block before:w-[4px] before:h-[4px] before:bg-text-and-icon-muted-2 before:rounded-full before:mr-2 before:absolute before:left-[0px] before:top-[9px] text-wrap`;

const searchShowTips = [
  'insights.shows_table.no_search_data.search_tip_1',
  'insights.shows_table.no_search_data.search_tip_2',
  'insights.shows_table.no_search_data.search_tip_3',
  'insights.shows_table.no_search_data.search_tip_4',
];

const searchEpisodeTips = [
  'insights.episodes_table.no_search_data.search_tip_1',
  'insights.episodes_table.no_search_data.search_tip_2',
  'insights.episodes_table.no_search_data.search_tip_3',
  'insights.episodes_table.no_search_data.search_tip_4',
];

const Show = (): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Container data-testid='show-no-data-fallback'>
      <NoResults />
      <Spacer direction='vertical' size='md' />
      <Title>{t('insights.shows_table.no_data.title')}</Title>
      <Spacer direction='vertical' size='sm' />
      <SmallText tw='text-center w-[280px]'>
        <Trans i18nKey='insights.shows_table.no_data.description'>
          If you don’t see list of shows here, pleas
          <a href='mailto:support@sounder.fm'>contact support</a>
        </Trans>
      </SmallText>
      <Spacer direction='vertical' size='xs' />
    </Container>
  );
};

const ShowSearchResult = (): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Container data-testid='show-no-search-data-fallback'>
      <NoSearchResultIcon fill={theme`fill.text-and-icon-muted-2`} />
      <Spacer direction='vertical' size='md' />
      <Title>{t('insights.shows_table.no_search_data.title')}</Title>
      <Spacer direction='vertical' size='sm' />
      <Card>
        <SmallText tw='font-semibold'>
          {t('insights.shows_table.no_search_data.sub_title')}
        </SmallText>
        <Spacer direction='vertical' size='xs' />
        <List>
          {searchShowTips.map((item) => (
            <ListItem key={item}>{t(item)}</ListItem>
          ))}
        </List>
      </Card>
    </Container>
  );
};

const Episode = (): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Container data-testid='episode-no-data-fallback'>
      <NoDataIcon />
      <Spacer direction='vertical' size='md' />
      <Title>{t('insights.episodes_table.no_data.title')}</Title>
      <Spacer direction='vertical' size='sm' />
      <SmallText tw='text-center w-[320px]'>
        <Trans i18nKey='insights.episodes_table.no_data.description'>
          The episodes might not be imported or processed yet. If you have questions please
          <a href='mailto:support@sounder.fm'>contact support</a>
        </Trans>
      </SmallText>
      <Spacer direction='vertical' size='xs' />
    </Container>
  );
};

const EpisodeSearchResult = (): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Container data-testid='episode-no-search-data-fallback'>
      <NoSearchResultIcon fill={theme`fill.text-and-icon-muted`} />
      <Spacer direction='vertical' size='md' />
      <Title>{t('insights.episodes_table.no_search_data.title')}</Title>
      <Spacer direction='vertical' size='sm' />
      <Card>
        <SmallText tw='font-semibold'>
          {t('insights.episodes_table.no_search_data.sub_title')}
        </SmallText>
        <Spacer direction='vertical' size='xs' />
        <List>
          {searchEpisodeTips.map((item) => (
            <ListItem key={item}>{t(item)}</ListItem>
          ))}
        </List>
      </Card>
    </Container>
  );
};

// Use Shows component as the default fallback
export const NoTableData = (): JSX.Element => <Show />;

NoTableData.Show = Show;
NoTableData.ShowSearchResult = ShowSearchResult;
NoTableData.Episode = Episode;
NoTableData.EpisodeSearchResult = EpisodeSearchResult;
